import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import type {
  LearnCardDeparturePose,
  LearnCardRelease,
  LearnCardReleaseMode,
  LearnCardStalledRelease,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useMemo } from "react";

/** Run on the UI thread: retirement and geometry reset form a single frame transaction. */
export function resetLearnDeckMotion(
  stage: LearnDeckStageValues,
  face: "front" | "back",
  presentationKey: string,
  epoch: number,
) {
  "worklet";
  const previous = stage.motionPresentation.get();
  stage.motionPresentation.set({
    current: presentationKey,
    dismissed: previous.current === presentationKey ? previous.dismissed : previous.current,
  });
  stage.motionEpoch.set(epoch);
  // A promotion sets the departure again right after this reset; anything else (a history
  // restore, a new session) ends a flight that is under way.
  stage.departure.set(null);
  stage.departureProgress.set(0);
  stage.release.set(null);
  stage.stalledRelease.set(null);
  stage.committedPresentation.set(null);
  stage.gestureId.set(stage.gestureId.get() + 1);
  stage.isDragging.set(false);
  stage.exitingOperation.set(null);
  stage.dragX.set(0);
  stage.dragY.set(0);
  stage.tilt.set(0);
  stage.exitProgress.set(0);
  stage.flipProgress.set(face === "back" ? 1 : 0);
}

/** One set of UI-thread values for the whole deck; the current card and its successor share it. */
export function useLearnDeckStageValues(presentationKey: string): LearnDeckStageValues {
  const motionPresentation = useSharedValue<{ current: string; dismissed: string | null }>({
    current: presentationKey,
    dismissed: null,
  });
  const cardHeight = useSharedValue(0);
  const cardWidth = useSharedValue(0);
  const committedPresentation = useSharedValue<string | null>(null);
  const dragX = useSharedValue(0);
  const dragY = useSharedValue(0);
  const departure = useSharedValue<LearnCardDeparturePose | null>(null);
  const departureProgress = useSharedValue(0);
  const exitProgress = useSharedValue(0);
  const flipProgress = useSharedValue(0);
  const isSuccessorReady = useSharedValue(false);
  const isDragging = useSharedValue(false);
  const exitingOperation = useSharedValue<string | null>(null);
  const gestureId = useSharedValue(0);
  const motionEpoch = useSharedValue(0);
  const pageStride = useSharedValue(0);
  const release = useSharedValue<LearnCardRelease | null>(null);
  // Only the deck flies a released card; any other card (the Learn summary's) parks it.
  const releaseMode = useSharedValue<LearnCardReleaseMode>("park");
  const stalledRelease = useSharedValue<LearnCardStalledRelease | null>(null);
  const successorPresentation = useSharedValue<string | null>(null);
  const tilt = useSharedValue(0);

  return useMemo(
    () => ({
      cardHeight,
      cardWidth,
      committedPresentation,
      departure,
      departureProgress,
      dragX,
      dragY,
      exitingOperation,
      exitProgress,
      flipProgress,
      gestureId,
      isDragging,
      isSuccessorReady,
      motionEpoch,
      motionPresentation,
      pageStride,
      release,
      releaseMode,
      stalledRelease,
      successorPresentation,
      tilt,
    }),
    [
      cardHeight,
      cardWidth,
      committedPresentation,
      dragX,
      dragY,
      departure,
      departureProgress,
      exitProgress,
      flipProgress,
      isSuccessorReady,
      isDragging,
      exitingOperation,
      gestureId,
      motionEpoch,
      motionPresentation,
      pageStride,
      release,
      releaseMode,
      stalledRelease,
      successorPresentation,
      tilt,
    ],
  );
}
